import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, Grid, Button, TextareaAutosize } from '@material-ui/core';
import { CircularLoading } from 'views/components/utils/circular-loading/circular-loading';
import { TextFieldSaurus } from 'views/components/controles/inputs';
import {
  DefaultFormProps,
  DefaultFormRefs,
} from 'views/components/form/utils/form-default-props';
import { useThemeQueries, makeUtilClasses } from 'views';
import { Controller, useForm } from 'react-hook-form';
import { KeyValueModel } from 'model';
import { SelectSaurus } from 'views/components/controles/selects/select-saurus/select-saurus';
import { useFormCadastrarPropagandaValidation } from './form-propaganda-cadastro-validation'
import { yupResolver } from '@hookform/resolvers/yup';
import { useStyles } from './propaganda-cadastro-styles'
import { PropagandaLocalMock } from 'data/mocks/propaganda-local-mock';
import { PropagandaFormFields } from 'model/app/forms/propaganda/propaganda-form-fields';
import { EnumTipoPropagandaLocal } from 'model/enums/enum-tipo-propaganda-local';
import { EnumTipoPropagandaDestaque } from 'model/enums/enum-tipo-propaganda-destaque';
import { PropagandaDestaqueMock } from 'data/mocks/propaganda-destaque-mock';
import ExibicaoHtml from './components/dialog-exibicao-html/dialog-exibicao-html';
import { useSessaoAtual } from 'services/app';
import { DialogCarregarTemplateHtml } from './components/dialog-carregar-template-html/dialog-carregar-template-html';

interface Props extends DefaultFormProps<PropagandaFormFields> {
  disabled?: boolean;
}
export const FormPropagandaCadastro = forwardRef<
  DefaultFormRefs<PropagandaFormFields>,
  Props
>(
  (
    { loading, onSubmit, ...props }: Props,
    ref,
  ) => {
    const { isMobile } = useThemeQueries();
    const utilClasses = makeUtilClasses();
    const refInputNome = useRef<HTMLInputElement>(null);
    const { FormCadastrarPlanoValidationYup } = useFormCadastrarPropagandaValidation()
    const classes = useStyles();
    const [exibirHtml, setExibirHtml] = useState(false);
    const { usuario } = useSessaoAtual()
    const [dialogOpen, setDialogOpen] = useState(false);

    const {
      handleSubmit,
      control,
      formState: { errors },
      reset,
      setValue,
      getValues,
      watch
    } = useForm<PropagandaFormFields>({
      resolver: yupResolver(FormCadastrarPlanoValidationYup),
      criteriaMode: 'all',
      mode: 'onChange',
      defaultValues: {
        descricao: '',
        html: '',
        dtInicial: '',
        dtFinal: '',
        local: EnumTipoPropagandaLocal.Ambos,
        destaque: EnumTipoPropagandaDestaque.NaoInformado,
        ativo: true,
        tempoExibicao: 0,
        tempoMinimo: 0,
        ignoraVisualizacao: false,
        respostasDisponiveis: ''
      }
    });

    const submit = (values: PropagandaFormFields) => {

      onSubmit(values);
    };

    useImperativeHandle(ref, () => ({
      submitForm: async () => {
        let foiChamado = false;

        await handleSubmit(
          async (values) => {
            foiChamado = true;
            await onSubmit(values);
          },
          () => {
            foiChamado = false;
          }
        )();

        return foiChamado;
      },
      resetForm: () => {
        reset(new PropagandaFormFields());
        if (!isMobile) refInputNome.current?.focus();
      },
      fillForm: (model: PropagandaFormFields) => {
        reset({ ...model });
        if (!isMobile) refInputNome.current?.focus();
      },
    }));

    useEffect(() => {
      watch(('tempoExibicao'));
    }, [watch]);

    useEffect(() => {
      if (usuario?.RevendaId) {
        setValue('revendaId', usuario.RevendaId);
      }
    }, [setValue, usuario])

    const templateHTML = `<html>

<head>
  <style>
    html,
    body,
    head,
    #divGeral {
      margin: 0px;
      padding: 0px;
      width: 100%;
      height: 100%;
      background: #fff;
    }

    .divImgFundo {
      width: 100%;
      height: 100%;
      background-image: url(INSIRA A URL DA IMAGEM AQUI (2268x1080));
      filter: progid:DXImageTransform.Microsoft.AlphaImageLoader(src='INSIRA A URL DA IMAGEM AQUI (2268x1080)', sizingMethod='scale');
      -ms-filter: "progid:DXImageTransform.Microsoft.AlphaImageLoader(src='INSIRA A URL DA IMAGEM AQUI (2268x1080)', sizingMethod='scale')";
      background-color: #fff;
      background-size: 1150px auto;
      background-repeat: no-repeat;
      background-position: center;
    }
    
    a {
      color: #FFF;
      text-decoration: none;
    }
    a:hover {
      color: #FFF;
      text-decoration: underline;
    }
    a:visited {
      color: #FFF;
      text-decoration: none;
    }
  </style>
</head>

<body>
  <div class="divImgFundo"></div>
</body>

</html>`

    const onSubmitCarregarTemplateHtml = (url: string) => {
      setValue('html', templateHTML.replaceAll('INSIRA A URL DA IMAGEM AQUI (2268x1080)', url));
    }

    return (
      <>
        <Box className={classes.formCadastro}>
          <div className={utilClasses.formContainer}>
            {loading && props.showLoading ? (
              <div className={utilClasses.controlLoading}>
                <CircularLoading tipo="FULLSIZED" />
              </div>
            ) : null}

            <form
              onSubmit={handleSubmit(submit)}
              className={loading ? utilClasses.controlLoading : ''}
            >
              <Box p={1} ml={1}>
                <Grid container spacing={2} justifyContent="center" style={{ width: '100%' }}>
                  <Grid item xs={12} md={8}>
                    <Controller
                      name="descricao"
                      control={control}
                      render={({ field }) => (
                        <TextFieldSaurus
                          inputRef={refInputNome}
                          disabled={props.disabled || loading}
                          allowSubmit={false}
                          id="descricao"
                          label="Descrição"
                          fullWidth
                          autoComplete={'off'}
                          helperText={
                            errors.descricao
                              ? errors.descricao.message
                              : undefined
                          }
                          error={Boolean(errors.descricao && errors.descricao.message)}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Controller
                      name="tempoExibicao"
                      control={control}
                      render={({ field }) => (
                        <TextFieldSaurus
                          disabled={props.disabled || loading}
                          allowSubmit={false}
                          label="Tempo de Exibição"
                          fullWidth
                          tipo='NUMERO'
                          autoComplete={'off'}
                          helperText={
                            errors.tempoExibicao
                              ? errors.tempoExibicao.message
                              : undefined
                          }
                          error={Boolean(errors.tempoExibicao && errors.tempoExibicao.message)}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Controller
                      name="tempoMinimo"
                      control={control}
                      render={({ field }) => (
                        <TextFieldSaurus
                          disabled={loading || props.disabled}
                          allowSubmit={false}
                          label="Tempo Mínimo"
                          fullWidth
                          tipo='NUMERO'
                          autoComplete={'off'}
                          helperText={
                            errors.tempoMinimo
                              ? errors.tempoMinimo.message
                              : undefined
                          }
                          error={Boolean(errors.tempoMinimo && errors.tempoMinimo.message)}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="dtInicial"
                      control={control}
                      render={({ field }) => (
                        <TextFieldSaurus
                          disabled={loading || props.disabled}
                          allowSubmit={false}
                          label="Data Inicial"
                          fullWidth
                          tipo='DATA_HORA'
                          autoComplete={'off'}
                          helperText={
                            errors.dtInicial
                              ? errors.dtInicial.message
                              : undefined
                          }
                          error={Boolean(errors.dtInicial && errors.dtInicial.message)}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Controller
                      name="dtFinal"
                      control={control}
                      render={({ field }) => (
                        <TextFieldSaurus
                          disabled={loading || props.disabled}
                          allowSubmit={false}
                          label="Data Final"
                          fullWidth
                          tipo='DATA_HORA'
                          autoComplete={'off'}
                          helperText={
                            errors.dtFinal
                              ? errors.dtFinal.message
                              : undefined
                          }
                          error={Boolean(errors.dtFinal && errors.dtFinal.message)}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4} md={2}>
                    <Controller
                      name="local"
                      control={control}
                      render={({ field }) => (
                        <SelectSaurus
                          conteudo={PropagandaLocalMock}
                          disabled={loading || props.disabled}
                          allowSubmit={false}
                          label="Local"
                          fullWidth
                          autoComplete={'off'}
                          helperText={
                            errors.local
                              ? errors.local.message
                              : undefined
                          }
                          error={Boolean(errors.local && errors.local.message)}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4} md={2}>
                    <Controller
                      name="destaque"
                      control={control}
                      render={({ field }) => (
                        <SelectSaurus
                          conteudo={PropagandaDestaqueMock}
                          disabled={loading || props.disabled}
                          allowSubmit={false}
                          label="Destaque"
                          fullWidth
                          autoComplete={'off'}
                          helperText={
                            errors.destaque
                              ? errors.destaque.message
                              : undefined
                          }
                          error={Boolean(errors.destaque && errors.destaque.message)}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4} md={2}>
                    <Controller
                      name="ativo"
                      control={control}
                      render={({ field }) => (
                        <SelectSaurus
                          conteudo={[
                            new KeyValueModel(true, 'Ativo'),
                            new KeyValueModel(false, 'Inativo')
                          ]}
                          disabled={loading || props.disabled}
                          allowSubmit={false}
                          label="Situação"
                          fullWidth
                          autoComplete={'off'}
                          helperText={
                            errors.ativo
                              ? errors.ativo.message
                              : undefined
                          }
                          error={Boolean(errors.ativo && errors.ativo.message)}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      control={control}
                      name='respostasDisponiveis'
                      render={({ field }) => (
                        <TextFieldSaurus
                          {...field}
                          tipo="TEXTO"
                          fullWidth
                          error={Boolean(errors.respostasDisponiveis && errors.respostasDisponiveis.message)}
                          allowSubmit
                          disabled={props.disabled || loading}
                          helperText={
                            errors.respostasDisponiveis
                              ? errors.respostasDisponiveis?.message
                              : undefined
                          }
                          placeholder="Exemplo: Interessado;Não interessado"
                          label='Opções de Resposta'
                          value={getValues('respostasDisponiveis')}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Controller
                      name="ignoraVisualizacao"
                      control={control}
                      render={({ field }) => (
                        <SelectSaurus
                          conteudo={[
                            new KeyValueModel(true, 'Sim'),
                            new KeyValueModel(false, 'Não')
                          ]}
                          disabled={loading || props.disabled}
                          allowSubmit={false}
                          label="Ignora Visualização"
                          fullWidth
                          autoComplete={'off'}
                          helperText={
                            errors.ignoraVisualizacao
                              ? errors.ignoraVisualizacao.message
                              : undefined
                          }
                          error={Boolean(errors.ignoraVisualizacao && errors.ignoraVisualizacao.message)}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={12} className={classes.textAreaContainer}>
                    <label className={`MuiFormLabel-root MuiInputLabel-root MuiInputLabel-formControl MuiInputLabel-animated MuiInputLabel-shrink MuiInputLabel-outlined ${classes.htmlLabel}`}>
                      HTML
                    </label>
                    <Controller
                      name="html"
                      control={control}
                      render={({ field }) => (
                        <TextareaAutosize
                          maxRows={15}
                          disabled={loading || props.disabled}
                          minRows={15}
                          className={classes.textareaHTML}
                          {...field}
                          value={getValues('html')}
                          onChange={(e: any) => {
                            setValue('html', e.target.value)
                          }}
                        />
                      )}
                    />
                    <Box width="100%" display="flex" justifyContent="flex-end" my={2}>
                      <Button onClick={() => {
                        setDialogOpen(true);
                      }}>
                        Carregar Template HTML
                      </Button>
                    </Box>
                    <Button color='secondary' variant='contained' size="large" fullWidth onClick={() => {
                      setExibirHtml(true);
                    }}>
                      Visualizar HTML
                    </Button>
                  </Grid>
                  <ExibicaoHtml openned={exibirHtml} onClose={() => setExibirHtml(false)} html={exibirHtml ? getValues('html') : ''}
                  />
                </Grid>
              </Box>
            </form>
          </div>
        </Box>
        <DialogCarregarTemplateHtml onSubmit={onSubmitCarregarTemplateHtml} open={dialogOpen} setOpen={setDialogOpen} />
      </>
    );
  },
);